/**
 * Čistá geometrie canvasu — bez závislosti na React Flow, snadno testovatelná.
 */
import type { ColumnModel, TableModel, ViewSettings } from '@bits2schema/shared';

/** Odpovídá `.table-node` v styles.css. */
export const TABLE_NODE_WIDTH = 240;
export const TABLE_HEADER_HEIGHT = 36;
export const TABLE_COLUMN_ROW_HEIGHT = 26;
/** Použito jen jako fallback pro tabulku bez sloupců. */
export const TABLE_NODE_FALLBACK_HEIGHT = 90;

/**
 * Svislý rozestup mezi poznámkami naskládanými u stejné tabulky/skupiny
 * (`CanvasView.tsx`'s derive efekt, Epic 6/7) — sdíleno i se statickým HTML
 * exportem (Epic 12, `export/htmlExport.ts`), který musí naskládat volné/
 * připnuté poznámky úplně stejně, aby diagram věrně odpovídal editoru.
 */
export const NOTE_STACK_OFFSET = 116;

/** PK i FK sloupce se počítají jako „klíčové" — viz Epic 8 (showNonKeyColumns). */
export function isKeyColumn(column: ColumnModel, table: TableModel): boolean {
  if (table.primaryKey?.columns.includes(column.name)) return true;
  return table.foreignKeys.some((foreignKey) => foreignKey.columns.includes(column.name));
}

/**
 * Jedna podmínka viditelnosti sloupce, sdílená mezi vykreslením řádku
 * A viditelností jeho značky poznámky (epic-6 kap. 8: „jedna podmínka, ne dvě").
 */
export function isColumnVisible(
  column: ColumnModel,
  table: TableModel,
  viewSettings: ViewSettings,
): boolean {
  return viewSettings.showNonKeyColumns || isKeyColumn(column, table);
}

export function visibleColumns(table: TableModel, viewSettings: ViewSettings): ColumnModel[] {
  return table.columns.filter((column) => isColumnVisible(column, table, viewSettings));
}

/**
 * Svislý střed řádku daného sloupce (od horního okraje table node) — pro
 * ukotvení FK hrany na konkrétní řádek místo středu tabulky (Epic 5,
 * `ForeignKeyEdge.tsx`). Vrací `null`, pokud sloupec aktuálně není
 * vykreslený jako řádek (skrytý přepínačem `showNonKeyColumns`, Epic 8) —
 * volající pak spadne zpět na svislý střed tabulky.
 */
export function columnRowCenterOffset(
  table: TableModel,
  columnId: string,
  viewSettings: ViewSettings,
): number | null {
  const rows = visibleColumns(table, viewSettings);
  const index = rows.findIndex((column) => column.id === columnId);
  if (index === -1) return null;
  return TABLE_HEADER_HEIGHT + index * TABLE_COLUMN_ROW_HEIGHT + TABLE_COLUMN_ROW_HEIGHT / 2;
}

/** Výška table node se počítá analyticky (žádné DOM měření) — musí odpovídat CSS konstantám výše. */
export function estimateTableNodeHeight(table: TableModel, viewSettings: ViewSettings): number {
  const rows = visibleColumns(table, viewSettings).length;
  return rows === 0
    ? TABLE_NODE_FALLBACK_HEIGHT
    : TABLE_HEADER_HEIGHT + rows * TABLE_COLUMN_ROW_HEIGHT;
}

const GRID_GAP_X = 80;
const GRID_GAP_Y = 60;
const GRID_COLUMNS = 4;

/**
 * Výchozí pozice pro tabulku bez `tablePositions` záznamu (nová/importovaná
 * bez explicitní pozice) — jednoduchá kaskáda do mřížky, ne skutečný
 * auto-layout (ten je Epic 7, E7-US1). Cílem je jen nepřekrývat tabulky.
 */
export function fallbackTablePosition(index: number): { x: number; y: number } {
  const column = index % GRID_COLUMNS;
  const row = Math.floor(index / GRID_COLUMNS);
  return {
    x: column * (TABLE_NODE_WIDTH + GRID_GAP_X),
    y: row * (TABLE_NODE_FALLBACK_HEIGHT + GRID_GAP_Y) * 2,
  };
}

export interface BoxSize {
  width: number;
  height: number;
}

export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

const GROUP_PADDING = 32;
/** Místo nad rámečkem pro štítek s názvem skupiny. */
const GROUP_LABEL_HEIGHT = 28;

/**
 * Obálka skupiny se nikdy nepersistuje — dopočítává se vždy znovu z pozic
 * (a velikostí) členských tabulek (epic-6 kap. 6/8). Vrací `null`, pokud
 * skupina nemá žádného člena se známou pozicí (typicky prázdná skupina).
 */
export function computeGroupBoundingBox(
  memberTableIds: string[],
  tablePositions: Record<string, { x: number; y: number }>,
  tableSizes: Record<string, BoxSize>,
  padding = GROUP_PADDING,
): BoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const tableId of memberTableIds) {
    const position = tablePositions[tableId];
    if (!position) continue;
    const size = tableSizes[tableId] ?? { width: TABLE_NODE_WIDTH, height: TABLE_NODE_FALLBACK_HEIGHT };
    minX = Math.min(minX, position.x);
    minY = Math.min(minY, position.y);
    maxX = Math.max(maxX, position.x + size.width);
    maxY = Math.max(maxY, position.y + size.height);
  }

  if (!Number.isFinite(minX)) return null;

  return {
    x: minX - padding,
    y: minY - padding - GROUP_LABEL_HEIGHT,
    width: maxX - minX + padding * 2,
    height: maxY - minY + padding * 2 + GROUP_LABEL_HEIGHT,
  };
}

export interface Point {
  x: number;
  y: number;
}

/**
 * Geometrie FK hrany (Epic 5/7 „floating edge" + waypoint polyline) — sdíleno
 * mezi `ForeignKeyEdge.tsx` (živé vykreslení v React Flow) a statickým HTML
 * exportem (Epic 12, `export/htmlExport.ts`), aby diagram v exportu vypadal
 * PŘESNĚ jako na canvasu, ne jen podobně přepočítaný nanovo.
 */
export interface EdgeEndpoint {
  positionAbsolute: Point;
  size: BoxSize;
  /** Svislý střed řádku sloupce (`columnRowCenterOffset`), nebo `null` pro střed tabulky. */
  rowOffset: number | null;
}

export interface EdgeVisual {
  /** SVG `d` atribut — polyline skrz waypointy, nebo (self-referenční bez waypointů) bezier smyčka. */
  path: string;
  sourceLabelPoint: Point;
  sourceLabelDx: number;
  targetLabelPoint: Point;
  targetLabelDx: number;
}

/** Standardní „floating edge" výpočet — průsečík úsečky mezi středy s okrajem obdélníku uzlu. */
export function rectIntersection(center: Point, size: BoxSize, towards: Point): Point {
  const dx = towards.x - center.x;
  const dy = towards.y - center.y;
  if (dx === 0 && dy === 0) return center;
  const scaleX = dx !== 0 ? size.width / 2 / Math.abs(dx) : Number.POSITIVE_INFINITY;
  const scaleY = dy !== 0 ? size.height / 2 / Math.abs(dy) : Number.POSITIVE_INFINITY;
  const scale = Math.min(scaleX, scaleY);
  return { x: center.x + dx * scale, y: center.y + dy * scale };
}

/** Vodorovná pozice na okraji obdélníku uzlu v dané výšce `y` (`y` je absolutní, ne relativní k uzlu). */
export function edgeXAt(node: { positionAbsolute: Point; size: BoxSize }, y: number, facing: 'left' | 'right'): Point {
  const x = facing === 'right' ? node.positionAbsolute.x + node.size.width : node.positionAbsolute.x;
  return { x, y };
}

export function rowY(node: { positionAbsolute: Point; size: BoxSize }, offset: number | null): number {
  return node.positionAbsolute.y + (offset ?? node.size.height / 2);
}

/** Jeden SVG path string skrz `[start, ...waypoints, end]` — funguje identicky se
 * `getStraightPath` i s 0 waypointy. */
export function buildPolylinePath(points: Point[]): string {
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
}

/**
 * Vrací hotovou SVG cestu + ukotvující body pro kardinalitní štítky (Epic 5,
 * E5-US2). Self-referenční FK (`isSelfReference`) beze waypointů kreslí
 * bezier smyčku vpravo od tabulky (E5-US6); jinak vždy polyline skrz
 * `[from, ...waypoints, to]` (E7-US4).
 */
export function computeEdgeVisual(
  source: EdgeEndpoint,
  target: EdgeEndpoint,
  waypoints: Point[],
  isSelfReference: boolean,
): EdgeVisual {
  const sourceY = rowY(source, source.rowOffset);
  const targetY = rowY(target, target.rowOffset);

  if (isSelfReference) {
    const x = source.positionAbsolute.x + source.size.width;
    const loopStart: Point = { x, y: sourceY };
    const loopEnd: Point = { x, y: targetY };
    const path =
      waypoints.length > 0
        ? buildPolylinePath([loopStart, ...waypoints, loopEnd])
        : `M ${x} ${sourceY} C ${x + 56} ${sourceY} ${x + 56} ${targetY} ${x} ${targetY}`;
    return { path, sourceLabelPoint: loopStart, sourceLabelDx: 12, targetLabelPoint: loopEnd, targetLabelDx: 12 };
  }

  const sourceCenter: Point = {
    x: source.positionAbsolute.x + source.size.width / 2,
    y: source.positionAbsolute.y + source.size.height / 2,
  };
  const targetCenter: Point = {
    x: target.positionAbsolute.x + target.size.width / 2,
    y: target.positionAbsolute.y + target.size.height / 2,
  };
  const sourceFacing: 'left' | 'right' = targetCenter.x >= sourceCenter.x ? 'right' : 'left';
  const targetFacing: 'left' | 'right' = sourceCenter.x >= targetCenter.x ? 'right' : 'left';

  const from =
    source.rowOffset !== null ? edgeXAt(source, sourceY, sourceFacing) : rectIntersection(sourceCenter, source.size, targetCenter);
  const to =
    target.rowOffset !== null ? edgeXAt(target, targetY, targetFacing) : rectIntersection(targetCenter, target.size, sourceCenter);
  const path = buildPolylinePath([from, ...waypoints, to]);
  return {
    path,
    sourceLabelPoint: from,
    sourceLabelDx: sourceFacing === 'right' ? 12 : -12,
    targetLabelPoint: to,
    targetLabelDx: targetFacing === 'right' ? 12 : -12,
  };
}
